import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from './FormManager';
import { blogPosts } from '../blog-data'; // Import blog data
import './BlogPostPage.css';

const BlogPostPage = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { openGeneralForm } = useForm();
  const [isCopied, setIsCopied] = useState(false);

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleShareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blogPost.title)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleShareLinkedIn = () => {
    const url = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(blogPost.title)}&summary=${encodeURIComponent(blogPost.excerpt)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
    });
  };

  // Find the current blog post by slug
  const blogPost = blogPosts.find(post => post.slug === slug);

  // Set document title
  useEffect(() => {
    if (blogPost) {
      document.title = `${blogPost.title} | Enzlo`;
    }
  }, [blogPost]);

  // If no post is found, render a 404 or redirect
  if (!blogPost) {
    return <Navigate to="/404" />;
  }

  // Find related posts' full data
  const relatedPostsData = blogPost.relatedPosts
    .map(relatedId => blogPosts.find(post => post.id === relatedId))
    .filter(Boolean); // Filter out any undefined results

  // JSON-LD for BlogPosting
  const blogPostingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': blogPost.title,
    'image': {
      '@type': 'ImageObject',
      'url': `https://enzlo.com${blogPost.image}`,
      'width': blogPost.imageDimensions.width,
      'height': blogPost.imageDimensions.height
    },
    'author': {
      '@type': 'Person',
      'name': blogPost.author
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'Spoddig UG (haftungsbeschränkt)',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://enzlo.com/assets/logo.png'
      }
    },
    'datePublished': blogPost.dateISO,
    'dateModified': blogPost.dateISO,
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `https://enzlo.com/blog/${blogPost.slug}`
    },
    'description': blogPost.excerpt
  };

  // JSON-LD for BreadcrumbList
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://enzlo.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': 'https://enzlo.com/blog' },
      { '@type': 'ListItem', 'position': 3, 'name': blogPost.title, 'item': `https://enzlo.com/blog/${blogPost.slug}` }
    ]
  };

  // JSON-LD for FAQPage
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'Wie kann ein Handwerksbetrieb von lokaler SEO profitieren?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Lokale SEO sorgt dafür, dass Ihr Betrieb bei Google Maps und in den lokalen Suchergebnissen besser gefunden wird – mehr Sichtbarkeit, mehr Anfragen, mehr Kunden.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Welche Maßnahmen sind für lokale SEO besonders wichtig?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Google My Business optimieren, Branchenverzeichnisse nutzen, Bewertungen sammeln und lokale Keywords gezielt einsetzen.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Wie lange dauert es, bis lokale SEO wirkt?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Erste Verbesserungen sind meist nach wenigen Wochen sichtbar, nachhaltige Ergebnisse benötigen 3-6 Monate.'
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{`${blogPost.title} | Enzlo Blog`}</title>
        <meta name="description" content={blogPost.excerpt} />
        <link rel="canonical" href={`https://enzlo.com/blog/${blogPost.slug}`} />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${blogPost.title} | Enzlo Blog`} />
        <meta property="og:description" content={blogPost.excerpt} />
        <meta property="og:image" content={`https://enzlo.com${blogPost.image}`} />
        <meta property="og:url" content={`https://enzlo.com/blog/${blogPost.slug}`} />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${blogPost.title} | Enzlo Blog`} />
        <meta name="twitter:description" content={blogPost.excerpt} />
        <meta name="twitter:image" content={`https://enzlo.com${blogPost.image}`} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{JSON.stringify(blogPostingJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      
      {/* The rest of the component remains the same, using `blogPost` data */}
      {/* Table of Contents */}
      <nav className="blog-post-toc">
        <h4>Inhaltsverzeichnis</h4>
        <ul>
          {blogPost.tableOfContents.map(item => (
            <li key={item.id}>
              <a href={`#${item.id}`}>{item.title}</a>
            </li>
          ))}
        </ul>
      </nav>
      
      {/* Post Header */}
      <section className="blog-post-header">
        <div className="container">
          <div className="blog-post-header-content">
            <div className="blog-post-meta">
              <span className="blog-post-category">{blogPost.category.toUpperCase()}</span>
              <span className="blog-post-date">{blogPost.date}</span>
              <span className="blog-post-read-time">{blogPost.readTime} Lesezeit</span>
            </div>
            <h1 className="blog-post-title">
              {blogPost.title}
            </h1>
            
            <div className="blog-post-author">
              <picture>
                <source srcSet="/images/team/enzlo-team.webp" type="image/webp" />
                <img src="/images/team/enzlo-team.jpg" alt={t('authorImageAlt', 'Enzlo Team')} loading="lazy" className="blog-post-author-image" />
              </picture>
              <span className="blog-post-author-name">Von {blogPost.author}</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Image */}
      <section className="blog-post-featured-image">
        <div className="container">
          <picture>
            <source srcSet={blogPost.image.replace(/\.(jpg|jpeg|png)$/, '.webp')} type="image/webp" />
            <img src={blogPost.image} loading="lazy" alt={t('blogPostImageAlt', { title: blogPost.title })} className="blog-post-image" />
          </picture>
        </div>
      </section>
      
      {/* Blog Post Content */}
      <section className="blog-post-content-section">
        <div className="container">
          <div className="blog-post-layout">
            {/* Table of Contents Sidebar */}
            <aside className="blog-post-sidebar">
              <div className="blog-post-toc">
                <h3 className="blog-post-toc-title">Inhaltsverzeichnis</h3>
                <ul className="blog-post-toc-list">
                  {blogPost.tableOfContents.map((item) => (
                    <li key={item.id} className="blog-post-toc-item">
                      <a href={`#${item.id}`} className="blog-post-toc-link">
                        {item.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
            
            {/* Main Content */}
            <article className="blog-post-content">
              <p className="blog-post-intro">
                Als Handwerker in Süddeutschland – sei es als <a href="/dienstleistungen/online-marketing-elektriker" className="blog-post-link">Elektriker in München</a>, <a href="/dienstleistungen/online-marketing-klempner" className="blog-post-link">Klempner in Stuttgart</a> oder <a href="/dienstleistungen/online-marketing-landschaftsgaertner" className="blog-post-link">Landschaftsgärtner in Freiburg</a> – stehen Sie vor einer Herausforderung: Wie erreichen Sie Kunden, die nach Ihren Dienstleistungen suchen? Die Antwort liegt im lokalen SEO (Suchmaschinenoptimierung). Etwa <strong>75% der Kunden recherchieren online</strong>, bevor sie einen Handwerker beauftragen, und wenn Sie nicht in den Top-Suchergebnissen erscheinen, verlieren Sie potenzielle Aufträge an Ihre Konkurrenten. In diesem Artikel zeigen wir Ihnen, wie Sie die lokalen Suchergebnisse dominieren können, und wie <a href="/ueber-uns" className="blog-post-link">Enzlo</a> Ihnen dabei hilft, Ihre Online-Präsenz ohne Aufwand zu maximieren.
              </p>
              
              <h2 id="problem" className="blog-post-heading">Das Problem: Unsichtbarkeit kostet Kunden</h2>
              <p>
                Viele Handwerker verlassen sich auf Mundpropaganda oder traditionelle Werbung, doch die Realität hat sich verändert. Laut allgemeinen Markttrends suchen etwa <strong>75% der Kunden online nach lokalen Dienstleistungen</strong>, bevor sie eine Entscheidung treffen. Wenn Sie bei Suchanfragen wie „Elektriker in München" oder „Klempner in Nürnberg" nicht auf der ersten Seite von Google erscheinen, verlieren Sie Kunden – oft ohne es zu merken.
              </p>
              <p>
                Die Konsequenzen sind gravierend:
              </p>
              <ul className="blog-post-list">
                <li><strong>Umsatzverlust:</strong> Laut Studien können Handwerksbetriebe monatlich bis zu 2.400 € durch eine schlechte Online-Präsenz verlieren.</li>
                <li><strong>Konkurrenzvorteil:</strong> Wettbewerber mit optimierten Websites und Google-Profilen schnappen Ihnen Kunden weg.</li>
                <li><strong>Zeitdruck:</strong> Kunden entscheiden oft innerhalb von 5 Sekunden, ob sie ein Unternehmen kontaktieren – Ihr erster Eindruck zählt!</li>
              </ul>
              <p>
                <a href="/dienstleistungen/lokales-seo" className="blog-post-link">Lokales SEO</a> ist die Lösung, um diese Probleme zu überwinden. Es sorgt dafür, dass Ihr Unternehmen genau dann gefunden wird, wenn potenzielle Kunden nach Ihren Dienstleistungen suchen.
              </p>
              
              <h2 id="google-my-business" className="blog-post-heading">Google My Business: Ihr Tor zur lokalen Sichtbarkeit</h2>
              <p>
                <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="blog-post-link">Google My Business (GMB)</a> ist ein kostenloses Tool, das Ihr Unternehmen in Google-Suchergebnissen und auf Google Maps präsentiert. Ein optimiertes GMB-Profil ist der Schlüssel, um im sogenannten Local Map Pack – den drei obersten Ergebnissen auf Google Maps – zu erscheinen. Hier ist eine Schritt-für-Schritt-Anleitung zur Optimierung:
              </p>
              <ol className="blog-post-list blog-post-list-numbered">
                <li><strong>Profil anlegen oder beanspruchen:</strong> Gehen Sie zu business.google.com und erstellen Sie ein Profil oder beanspruchen Sie ein bestehendes.</li>
                <li><strong>Alle Informationen ausfüllen:</strong> Geben Sie Ihren Unternehmensnamen, Adresse, Telefonnummer, Öffnungszeiten und eine detaillierte Beschreibung ein. Erwähnen Sie spezifische Dienstleistungen, z. B. „Elektroinstallationen in München".</li>
                <li><strong>Hochwertige Fotos hinzufügen:</strong> Laden Sie Bilder Ihres Teams, Ihrer Arbeiten und Ihres Betriebs hoch. Kunden vertrauen visuellen Inhalten mehr.</li>
                <li><strong>Regelmäßige Posts:</strong> Nutzen Sie die Post-Funktion, um Angebote, Neuigkeiten oder Tipps zu teilen, z. B. „Winter-Check für Ihre Heizung in Bayern".</li>
                <li><strong>Rezensionen beantworten:</strong> Reagieren Sie auf alle Bewertungen – positive wie negative – professionell und zeitnah, um Vertrauen aufzubauen.</li>
              </ol>
              <div className="blog-post-callout">
                <p>Ein gut gepflegtes GMB-Profil kann Ihre Sichtbarkeit innerhalb von 2–6 Wochen deutlich steigern, besonders in weniger wettbewerbsintensiven Märkten wie kleineren Städten in Süddeutschland.</p>
              </div>
              
              <h2 id="lokale-citations" className="blog-post-heading">Lokale Citations: Vertrauen und Konsistenz aufbauen</h2>
              <p>
                Lokale Citations sind Erwähnungen Ihres Unternehmens (Name, Adresse, Telefonnummer – NAP) auf anderen Websites, insbesondere in Branchenverzeichnissen. Sie signalisieren Google, dass Ihr Unternehmen legitim und lokal relevant ist. Hier sind die wichtigsten Schritte:
              </p>
              <ul className="blog-post-list">
                <li><strong>Einheitlichkeit sicherstellen:</strong> Ihre NAP-Daten müssen überall identisch sein, um Verwirrung zu vermeiden.</li>
                <li>
                  <strong>Wichtige Verzeichnisse in Deutschland:</strong>
                  <ul className="blog-post-sublist">
                    <li><a href="https://www.gelbeseiten.de" target="_blank" rel="noopener noreferrer" className="blog-post-link">Gelbe Seiten</a></li>
                    <li><a href="https://www.myhammer.de" target="_blank" rel="noopener noreferrer" className="blog-post-link">MyHammer</a></li>
                    <li><a href="https://www.check24.de" target="_blank" rel="noopener noreferrer" className="blog-post-link">Check24</a></li>
                    <li><a href="https://www.dasoertliche.de" target="_blank" rel="noopener noreferrer" className="blog-post-link">Das Örtliche</a></li>
                    <li><a href="https://www.branchenverzeichnis.de" target="_blank" rel="noopener noreferrer" className="blog-post-link">Branchenverzeichnis.de</a></li>
                  </ul>
                </li>
                <li><strong>Lokale Plattformen nutzen:</strong> Tragen Sie sich in regionalen Verzeichnissen ein, z. B. für <a href="/blog/lokale-verzeichnisse-muenchen" className="blog-post-link">München</a> oder <a href="/blog/lokale-verzeichnisse-stuttgart" className="blog-post-link">Stuttgart</a>, um die Relevanz zu erhöhen.</li>
              </ul>
              <p>
                Durch konsistente Citations können Sie Ihre lokale Autorität stärken und Ihre Chancen auf ein Top-Ranking im Local Map Pack verbessern.
              </p>
              
              <figure className="blog-post-figure">
                <picture>
                  <source srcSet="/images/blog/local-seo-map-pack.webp" type="image/webp" />
                  <img src="/images/blog/local-seo-map-pack.jpg" alt={t('localMapPackAlt', 'Google Local Map Pack Example')} loading="lazy" className="blog-post-figure-image" />
                </picture>
                <figcaption className="blog-post-figure-caption">Das Google Local Map Pack – die begehrte Position für lokale Unternehmen</figcaption>
              </figure>
              
              <h2 id="content-strategie" className="blog-post-heading">Content-Strategie: Relevante Inhalte für lokale Kunden</h2>
              <p>
                Eine professionelle Website ist das Herzstück Ihrer Online-Präsenz, aber sie muss mit lokal relevantem Content gefüllt sein, um Kunden anzuziehen. Hier sind einige Ideen für Inhalte, die Handwerker in Süddeutschland erstellen können:
              </p>
              <ul className="blog-post-list">
                <li>
                  <strong>Blog-Beiträge:</strong>
                  <ul className="blog-post-sublist">
                    <li>„Die häufigsten Sanitärprobleme in München und wie Sie sie lösen"</li>
                    <li>„Tipps für die Gartenpflege im schwäbischen Klima"</li>
                    <li>„Energieeffiziente Lösungen für Ihr Zuhause in Bayern"</li>
                  </ul>
                </li>
                <li><strong>Lokale Landingpages:</strong> Erstellen Sie Seiten für spezifische Dienstleistungen und Orte, z. B. „<a href="/dienstleistungen/elektriker-notdienst-freiburg" className="blog-post-link">Elektriker für Notfälle in Freiburg</a>".</li>
                <li><strong>Keyword-Optimierung:</strong> Verwenden Sie lokale Keywords wie „Klempner in Stuttgart" oder „Landschaftsgärtner in Augsburg" in Titeln, Überschriften und Texten.</li>
              </ul>
              <p>
                Solche Inhalte zeigen Ihr Fachwissen und erhöhen die Wahrscheinlichkeit, dass Sie für relevante Suchanfragen gefunden werden. Mehr dazu erfahren Sie in unserem Artikel <a href="/blog/content-marketing-expertise" className="blog-post-link">„Wie Sie mit Content-Marketing Ihre Expertise zeigen"</a>.
              </p>
              
              <h2 id="rezensionen" className="blog-post-heading">Die Macht der Rezensionen</h2>
              <p>
                Positive Kundenbewertungen sind ein entscheidender Faktor für lokales SEO. Sie beeinflussen nicht nur Ihre Rankings, sondern auch das Vertrauen potenzieller Kunden. Hier sind Tipps, um mehr Bewertungen zu sammeln:
              </p>
              <ul className="blog-post-list">
                <li><strong>Direkt fragen:</strong> Bitten Sie Kunden nach abgeschlossenen Arbeiten höflich um eine Bewertung.</li>
                <li><strong>Einfach machen:</strong> Fügen Sie einen direkten Link zu Ihrem GMB-Profil auf Rechnungen oder Visitenkarten hinzu.</li>
                <li><strong>Automatisierte Erinnerungen:</strong> Nutzen Sie E-Mail-Marketing, um Kunden an Bewertungen zu erinnern.</li>
              </ul>
              <p>
                Antworten Sie auf alle Bewertungen, um Professionalität zu zeigen. Ein Handwerksbetrieb mit 20+ positiven Bewertungen hat oft einen Vorteil gegenüber Konkurrenten mit wenigen oder keinen Bewertungen.
              </p>
              
              <div className="blog-post-quote">
                <blockquote>
                  "Die Optimierung unseres Google Business Profils und das aktive Bewertungsmanagement haben unsere Anfragen innerhalb von nur zwei Monaten um 40% gesteigert."
                </blockquote>
                <cite>— Thomas Weber, Elektrotechnik Weber, München</cite>
              </div>
              
              <h2 id="enzlo-loesung" className="blog-post-heading">Enzlo: Ihre All-in-One-Lösung</h2>
              <p>
                Als Handwerker haben Sie genug zu tun, ohne sich mit kompliziertem Online-Marketing herumschlagen zu müssen. Hier kommt <a href="/preise" className="blog-post-link">Enzlo</a> ins Spiel. Unsere Done-for-You-Dienste umfassen:
              </p>
              <ul className="blog-post-list">
                <li><strong>Professionelles Website-Design:</strong> Eine moderne, mobilfreundliche Website, die Besucher in Kunden verwandelt.</li>
                <li><strong>Lokale SEO-Optimierung:</strong> Von GMB bis zu lokalen Citations – wir sorgen für Top-Rankings.</li>
                <li><strong>Social Media Management:</strong> Konsistente Präsenz auf Plattformen wie Instagram, ohne Ihren Aufwand.</li>
                <li><strong>Bewertungsmanagement:</strong> Automatisierte Systeme, um Bewertungen zu sammeln und Vertrauen aufzubauen.</li>
                <li><strong>E-Mail-Marketing:</strong> Kampagnen, die Kunden zurückbringen.</li>
              </ul>
              <p>
                Mit Enzlo sparen Sie Zeit und gewinnen mehr Kunden, während Sie sich auf Ihr Handwerk konzentrieren können. <a href="/kontakt" className="blog-post-link">Vereinbaren Sie jetzt ein kostenloses Beratungsgespräch</a>.
              </p>
              
              <h2 id="erfolgsgeschichten" className="blog-post-heading">Erfolgsgeschichten: Handwerker, die mit Enzlo gewachsen sind</h2>
              <div className="blog-post-case-studies">
                <div className="blog-post-case-study">
                  <picture>
                    <source srcSet="/images/testimonials/elektriker-mueller.webp" type="image/webp" />
                    <img src="/images/testimonials/elektriker-mueller.jpg" alt={t('caseStudyMullerAlt', 'Mr. Müller, Electrician in Stuttgart')} loading="lazy" className="blog-post-case-study-image" />
                  </picture>
                  <div className="blog-post-case-study-content">
                    <h3>Herr Müller, Elektriker in Stuttgart</h3>
                    <p>Vor der Zusammenarbeit mit Enzlo hatte Herr Müller kaum Online-Sichtbarkeit. Nach der Optimierung seines GMB-Profils und der Erstellung einer neuen Website stieg seine Anfragezahl um 50% und sein Umsatz um 30% innerhalb von drei Monaten.</p>
                  </div>
                </div>
                <div className="blog-post-case-study">
                  <picture>
                    <source srcSet="/images/testimonials/friseurin-schmidt.webp" type="image/webp" />
                    <img src="/images/testimonials/friseurin-schmidt.jpg" alt={t('caseStudySchmidtAlt', 'Ms. Schmidt, Hairdresser in Nuremberg')} loading="lazy" className="blog-post-case-study-image" />
                  </picture>
                  <div className="blog-post-case-study-content">
                    <h3>Frau Schmidt, Friseurin in Nürnberg</h3>
                    <p>Durch Enzlos Social Media Management verdoppelte Frau Schmidt ihre Instagram-Follower und erhöhte ihre Buchungen um 40%.</p>
                  </div>
                </div>
              </div>
              <p>
                Diese Erfolge zeigen, wie Enzlo Handwerkern hilft, ihre Ziele zu erreichen. Weitere Erfolgsgeschichten finden Sie auf unserer <a href="/referenzen" className="blog-post-link">Referenzseite</a>.
              </p>
              
              <h2 id="fazit" className="blog-post-heading">Fazit: Jetzt ist die Zeit zu handeln</h2>
              <p>
                Lokales SEO ist kein Luxus, sondern eine Notwendigkeit für Handwerker in Süddeutschland. Mit den richtigen Strategien – von Google My Business über lokale Citations bis hin zu gezieltem Content – können Sie Ihre Sichtbarkeit steigern und Mehr Kunden gewinnen. Enzlo macht es Ihnen leicht, indem wir alles für Sie übernehmen.
              </p>
              <div className="blog-post-cta-box">
                <h3>Bereit, Ihre Online-Präsenz zu transformieren?</h3>
                <p>Kontaktieren Sie Enzlo für eine kostenlose Beratung und starten Sie noch heute durch!</p>
                <button className="blog-post-cta-button" onClick={openGeneralForm}>
                  <span>Kostenlose Beratung vereinbaren</span>
                  <span className="blog-post-cta-arrow">→</span>
                </button>
              </div>
              <div className="blog-post-contact">
                <p><strong>Kontaktieren Sie uns:</strong></p>
                <ul className="blog-post-contact-list">
                  <li><strong>Telefon:</strong> <a href="tel:+4915751619973" className="blog-post-link">+49 1575 161 9973</a></li>
                  <li><strong>E-Mail:</strong> <a href="mailto:kontakt@enzlo.com" className="blog-post-link">kontakt@enzlo.com</a></li>
                  <li><strong>Website:</strong> <a href="https://www.enzlo.com" className="blog-post-link">www.enzlo.com</a></li>
                </ul>
              </div>
              
              {/* Tags */}
              <div className="blog-post-tags">
                <span className="blog-post-tag">Lokales SEO</span>
                <span className="blog-post-tag">Handwerker</span>
                <span className="blog-post-tag">Google My Business</span>
                <span className="blog-post-tag">Online-Marketing</span>
                <span className="blog-post-tag">Süddeutschland</span>
              </div>
              
              {/* Share */}
              <div className="blog-post-share">
                <span className="blog-post-share-title">Teilen:</span>
                <div className="blog-post-share-buttons">
                  <button className="social-share-button" onClick={handleShareFacebook}>
                    <i className="fab fa-facebook"></i>
                    <span>{t('blogPost.shareFacebook')}</span>
                  </button>
                  <button className="social-share-button" onClick={handleShareTwitter}>
                    <i className="fab fa-twitter"></i>
                    <span>{t('blogPost.shareTwitter')}</span>
                  </button>
                  <button className="social-share-button" onClick={handleShareLinkedIn}>
                    <i className="fab fa-linkedin-in"></i>
                    <span>{t('blogPost.shareLinkedIn')}</span>
                  </button>
                  <button className={`social-share-button ${isCopied ? 'copied' : ''}`} onClick={handleCopyLink}>
                    <i className="fas fa-link"></i>
                    <span>{isCopied ? t('blogPost.linkCopied') : t('blogPost.copyLink')}</span>
                  </button>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>
      <section className="blog-post-longtail">
        <div className="container">
          <h2>Für wen geeignet?</h2>
          <ul>
            <li>Handwerksbetriebe, lokale Dienstleister, Bauunternehmen, Installateure, Elektriker, Maler</li>
            <li>Unternehmen in Frankfurt, Darmstadt, Stuttgart, Ulm, Aschaffenburg</li>
          </ul>
          <p><em>Ideal für alle, die mehr lokale Anfragen und Sichtbarkeit in Google suchen – z.B. "seo für handwerksbetriebe frankfurt", "handwerker google my business optimieren", "handwerker bewertungen frankfurt", oder "branchenverzeichnis handwerker süddeutschland".</em></p>
        </div>
      </section>

      {/* SEO-Checkliste für Handwerker */}
      <section className="blog-post-seo-checklist">
        <div className="container">
          <h2>SEO-Checkliste für Handwerker 2025</h2>
          <ul>
            <li><strong>Google My Business optimieren:</strong> Vollständig ausfüllen, richtige Kategorie wählen, regelmäßig aktualisieren.</li>
            <li><strong>Bewertungen sammeln:</strong> Kunden aktiv um Bewertungen bitten und auf Google präsentieren.</li>
            <li><strong>Branchenverzeichnisse nutzen:</strong> In relevante lokale Verzeichnisse eintragen (z.B. Handwerkskammer, MeinHandwerker, Gelbe Seiten).</li>
            <li><strong>NAP-Konsistenz:</strong> Name, Adresse, Telefonnummer überall identisch (Website, Google, Verzeichnisse).</li>
            <li><strong>Mobile Optimierung:</strong> Website responsiv und schnell laden lassen.</li>
            <li><strong>Lokale Keywords integrieren:</strong> Stadt/Region und Branche in Title, Meta, Überschriften, Fließtext und Alt-Text verwenden.</li>
            <li><strong>Interne Links:</strong> Zu <Link to="/kontakt">Kontakt</Link>, <Link to="/services#seo-frankfurt">SEO Frankfurt</Link>, <Link to="/services#webdesign-frankfurt">Webdesign Frankfurt</Link> und <Link to="/services#google-ads-frankfurt">Google Ads Frankfurt</Link> setzen.</li>
          </ul>
        </div>
      </section>

      {/* Wichtige lokale Keywords für Handwerker */}
      <section className="blog-post-keywords-table">
        <div className="container">
          <h2>Wichtige lokale Keywords für Handwerker in Süddeutschland</h2>
          <table className="keywords-table" style={{width: '100%', borderCollapse: 'collapse'}}>
            <thead>
              <tr>
                <th style={{border: '1px solid #ddd', padding: '8px'}}>Branche</th>
                <th style={{border: '1px solid #ddd', padding: '8px'}}>Keyword-Beispiel</th>
                <th style={{border: '1px solid #ddd', padding: '8px'}}>Stadt/Region</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>Dachdecker</td>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>dachdecker frankfurt, dachdeckerbetrieb darmstadt</td>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>Frankfurt, Darmstadt</td>
              </tr>
              <tr>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>Elektriker</td>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>elektriker stuttgart, elektroinstallation ulm</td>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>Stuttgart, Ulm</td>
              </tr>
              <tr>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>Maler</td>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>maler aschaffenburg, malerbetrieb frankfurt</td>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>Aschaffenburg, Frankfurt</td>
              </tr>
              <tr>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>Installateur</td>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>installateur ulm, sanitär stuttgart</td>
                <td style={{border: '1px solid #ddd', padding: '8px'}}>Ulm, Stuttgart</td>
              </tr>
            </tbody>
          </table>
          <p style={{marginTop: '1rem'}}>Nutzen Sie diese Keyword-Kombinationen gezielt in Ihren Texten, Meta-Tags und Alt-Texten, um in Ihrer Region besser gefunden zu werden.</p>
        </div>
      </section>
      <section className="blog-post-crosslinks">
        <div className="container">
          <h3>Weitere Leistungen für Ihren Erfolg:</h3>
          <ul style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem', listStyle: 'none', padding: 0}}>
            <li><Link to="/services#seo-frankfurt">SEO Frankfurt</Link></li>
            <li><Link to="/services#webdesign-frankfurt">Webdesign Frankfurt</Link></li>
            <li><Link to="/services#google-ads-frankfurt">Google Ads Frankfurt</Link></li>
          </ul>
        </div>
      </section>
      
      {/* Related Posts */}
      <section className="blog-post-related">
        <div className="container">
          <h2 className="blog-post-related-title">Das könnte Sie auch interessieren</h2>
          <div className="blog-post-related-grid">
            {relatedPostsData.map(post => (
              <article key={post.id} className="blog-post-related-card">
                <Link to={`/blog/${post.slug}`} className="blog-post-related-link">
                  <div className="blog-post-related-image-container">
                    <picture>
                      <source srcSet={post.image.replace(/\.(jpg|jpeg|png)$/, '.webp')} type="image/webp" />
                      <img src={post.image} alt={t('relatedPostImageAlt', { title: post.title })} loading="lazy" className="blog-post-related-image" />
                    </picture>
                  </div>
                  <h3 className="blog-post-related-card-title">{post.title}</h3>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="blog-post-faq">
        <div className="container">
          <h2>FAQ</h2>
          <div className="faq-list">
            <div className="faq-item">
              <h3>Wie kann ein Handwerksbetrieb von lokaler SEO profitieren?</h3>
              <div>Lokale SEO sorgt dafür, dass Ihr Betrieb bei Google Maps und in den lokalen Suchergebnissen besser gefunden wird – mehr Sichtbarkeit, mehr Anfragen, mehr Kunden.</div>
            </div>
            <div className="faq-item">
              <h3>Welche Maßnahmen sind für lokale SEO besonders wichtig?</h3>
              <div>Google My Business optimieren, Branchenverzeichnisse nutzen, Bewertungen sammeln und lokale Keywords gezielt einsetzen.</div>
            </div>
            <div className="faq-item">
              <h3>Wie lange dauert es, bis lokale SEO wirkt?</h3>
              <div>Erste Verbesserungen sind meist nach wenigen Wochen sichtbar, nachhaltige Ergebnisse benötigen 3-6 Monate.</div>
            </div>
            <div className="faq-item">
              <h3>Wie optimiere ich mein Google My Business als Handwerker?</h3>
              <div>Füllen Sie Ihr Profil vollständig aus, wählen Sie die passende Kategorie, fügen Sie aktuelle Fotos hinzu und bitten Sie Kunden aktiv um Bewertungen.</div>
            </div>
            <div className="faq-item">
              <h3>Welche Branchenverzeichnisse sind für Handwerker wichtig?</h3>
              <div>Handwerkskammer, MeinHandwerker, Gelbe Seiten, 11880.com und weitere regionale Verzeichnisse erhöhen Ihre Sichtbarkeit und stärken Ihr Google-Ranking.</div>
            </div>
            <div className="faq-item">
              <h3>Wie bekomme ich mehr lokale Bewertungen?</h3>
              <div>Bitten Sie zufriedene Kunden direkt nach Abschluss eines Auftrags um eine Bewertung auf Google oder anderen Plattformen. Nutzen Sie einen Bewertungslink zur Vereinfachung.</div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="blog-post-bottom-cta">
        <div className="container">
          <div className="blog-post-bottom-cta-content">
            <h2 className="blog-post-bottom-cta-title">{t('blogCtaTitle', 'Bereit, Ihre Online-Präsenz zu verbessern?')}</h2>
            <p className="blog-post-bottom-cta-description">{t('blogCtaDescription', 'Lassen Sie uns gemeinsam Ihre digitale Strategie entwickeln.')}</p>
            <button className="blog-post-bottom-cta-button" onClick={openGeneralForm}>
              <span className="blog-post-bottom-cta-text">{t('blogCtaPrimary', 'Kostenlose Beratung')}</span>
              <span className="blog-post-bottom-cta-arrow">→</span>
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPostPage;
